import { useCommonClient } from '@/app/common/commonClientProvider';
import { useSelectedMealPlan } from '@/app/components/hotel-detail/SelectedMealPlanProvider';
import { useUrlSearchParams } from '@/app/components/hotel-detail/UrlSearchParamsProvider';
import { useEvoModals } from '@/app/components/hotel-detail/evo/EvoModalsProvider';
import { getMealPlanCode } from '@/app/components/hotel-detail/rooms/roomCommon';
import { getRoomCancellationOptions } from '@/app/components/hotel-detail/rooms/RoomPanelCommon';
import { getPromotionGroupMealPlans } from '@/app/components/hotel-detail/evo/rooms/promotionGroup';
import { useTrackEvoBookClicked } from '@/app/components/hotel-detail/evo/hooks/useTrackEvoBookClicked';
import { useTrackEvoRoomSelection } from '@/app/components/hotel-detail/evo/hooks/useTrackEvoRoomSelection';

/**
 * MPH-935: the step the EVO rooms page advances to once a room and cancellation option are committed.
 *
 * On a package the next step is the flight page. Hotel Only has no flights to choose, so it goes straight
 * to the review step — the flight page there renders a permanent spinner and empty flight skeletons, and
 * emits flight telemetry for a journey that has none.
 *
 * Because Hotel Only skips the flight page, this is also where its book-click funnel step is emitted:
 * DETAIL_BOOK_CLICKED normally comes from the flight page's Book button (useTrackEvoBookClicked), and
 * trackingCheckoutStarted suppresses its own copy whenever evolutionEnabled — so without the call here
 * the variant would lose the event that the control arm still sends.
 *
 * Callers select and continue in one action, so the room and meal plan are passed explicitly: the
 * provider state set in the same handler is not readable here yet.
 */
export function useEvoGoToNextStepFromRooms() {
  const { accommodationOnly } = useUrlSearchParams();
  const { handleEvoGoToChangeTransport, handleEvoGoToCheckout } = useCommonClient();
  const trackEvoBookClicked = useTrackEvoBookClicked();

  return (room: RoomType, mealPlan: MealPlanType) => {
    const target = {
      roomOfferId: room.roomOfferId,
      mealPlanId: mealPlan.id,
      mealPlanCode: getMealPlanCode(mealPlan),
    };

    if (accommodationOnly) {
      trackEvoBookClicked(room, mealPlan);
      // No transportId: a hotel-only package carries no transport, and handleEvoGoToCheckout drops the
      // parameter from the review URL when it is absent.
      handleEvoGoToCheckout(target);
      return;
    }

    handleEvoGoToChangeTransport(target);
  };
}

/**
 * Shared "Continue" handler for the EVO rooms page (desktop trip-overview + mobile room cards).
 * When the room/board class offers more than one cancellation option, it opens the cancellation-type
 * selection modal so the user can choose; otherwise it advances straight to the next step.
 *
 * Defaults to the page-level selection (desktop trip-overview). Callers that select and continue in a
 * single action (the mobile room-card "Continue" button) can pass the room/meal plan explicitly, since
 * the provider state set in the same handler is not yet readable here.
 *
 * Tracking: HOTEL_ROOM_CLICKED + HOTEL_ROOM_OPTION_CLICKED fire here only when the room has a single
 * cancellation option and we advance directly. When there are several options the events are
 * deferred to the cancellation-type modal's own "Continue" (see EvoRoomCancellationTypeSelectionModal),
 * so they always reflect the cancellation option the user actually committed to.
 */
export function useEvoContinueFromRooms() {
  const { selectedRoom, selectedMealPlan } = useSelectedMealPlan();
  const { openCancellationTypeSelectionModal } = useEvoModals();
  const { trackRoomSelection } = useTrackEvoRoomSelection();
  const goToNextStep = useEvoGoToNextStepFromRooms();

  return (room?: RoomType, mealPlan?: MealPlanType) => {
    const targetRoom = room ?? selectedRoom;
    const targetMealPlan = mealPlan ?? selectedMealPlan;
    if (!targetRoom || !targetMealPlan) return;

    // Count only options of the target's own (board × promotion group) card and bed (MPH-652): counting
    // across groups would open the modal even when this group has a single cancellation option.
    const selectable = getPromotionGroupMealPlans(targetRoom.configurations, targetMealPlan).filter(
      (config) => config.bedId === targetMealPlan.bedId
    );
    const optionCount = Object.keys(getRoomCancellationOptions(selectable)).length;

    if (optionCount >= 2) {
      openCancellationTypeSelectionModal();
    } else {
      trackRoomSelection(targetRoom, targetMealPlan);
      goToNextStep(targetRoom, targetMealPlan);
    }
  };
}
